import { useState } from "react";
import {
  useMetricEvidence,
  type ComputedMetric,
  type EvidenceItem,
  type MetricSpec,
} from "../../hooks/useScoreboard";
import { useChunkContext } from "../../hooks/useChunkContext";

/**
 * ScoreboardEvidenceDrawer — slides in from the right and shows the ACTUAL
 * rows behind a metric's number. Each cited message / instrument / contradiction
 * can be expanded to its full source thread (via fetch_chunk_context), so a
 * count is never an unverifiable claim.
 */
export default function ScoreboardEvidenceDrawer({
  caseId,
  metric,
  onClose,
}: {
  caseId: string;
  metric: ComputedMetric | null;
  onClose: () => void;
}) {
  const spec: MetricSpec | null = metric
    ? { primitive: metric.primitive, label: metric.label, params: metric.params ?? {} }
    : null;
  const { data, isLoading, error } = useMetricEvidence(caseId, spec);

  if (!metric) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="flex h-full w-full max-w-xl flex-col border-l border-steel-dark bg-iron/95 shadow-2xl">
        <div className="flex items-start justify-between border-b border-steel-dark/70 px-5 py-4">
          <div>
            <p className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute">evidence behind</p>
            <p className="mt-0.5 font-display text-xl font-light text-bone">{metric.label}</p>
            <p className="mt-1 font-display text-sm font-light text-ember-bright">
              {metric.value ?? "—"} {metric.unit}
              {metric.pct != null ? ` · ${metric.pct}%` : ""}
            </p>
            {metric.why ? (
              <p className="mt-2 max-w-md font-body text-xs leading-snug text-bone-dim">{metric.why}</p>
            ) : null}
          </div>
          <button type="button" onClick={onClose} aria-label="close" className="font-display text-base text-bone-mute hover:text-bone">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <p className="font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute">// pulling the records…</p>
          ) : error ? (
            <p className="font-display text-sm text-ember-bright">// {error.message}</p>
          ) : !data || data.items.length === 0 ? (
            <p className="font-body text-sm text-bone-dim">
              No individual rows to show for this metric (it's an aggregate). The number is still computed from your real corpus.
            </p>
          ) : (
            <div className="space-y-3">
              <p className="font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute">
                // {data.items.length} cited{data.items.length === 40 ? "+" : ""}
              </p>
              {data.items.map((it, i) => (
                <EvidenceCard key={i} item={it} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EvidenceCard({ item }: { item: EvidenceItem }) {
  const [openChunk, setOpenChunk] = useState<string | null>(null);
  const accent =
    item.kind === "contradiction" ? "text-ember-bright" : item.kind === "authority" ? "text-blue-bright" : "text-bone";

  return (
    <div className="rounded border border-steel-dark/70 bg-black/30 px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <p className={`font-display text-base font-light leading-snug ${accent}`}>{item.title}</p>
        {item.date ? <span className="shrink-0 font-display text-sm font-light text-bone-mute">{item.date}</span> : null}
      </div>
      {item.subtitle ? (
        <p className="mt-0.5 font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute">{item.subtitle}</p>
      ) : null}
      {item.snippet ? (
        <p className="mt-1.5 font-body text-sm leading-snug text-bone-dim">{item.snippet}</p>
      ) : null}

      {/* nested contradicting evidence (for contradiction rows) */}
      {item.evidence && item.evidence.length > 0 ? (
        <div className="mt-3 space-y-2 border-l border-ember-bright/30 pl-3">
          {item.evidence.map((ev, j) => (
            <div key={j}>
              <p className="font-body text-xs italic leading-snug text-bone">&ldquo;{ev.quote}&rdquo;</p>
              <div className="mt-0.5 flex items-center gap-2">
                <span className="font-display text-xs font-light uppercase tracking-[0.08em] text-bone-mute">
                  {ev.source}{ev.date ? ` · ${ev.date}` : ""}
                </span>
                {ev.chunk_id ? (
                  <button type="button" onClick={() => setOpenChunk(openChunk === ev.chunk_id ? null : ev.chunk_id)}
                    className="font-display text-xs font-light uppercase tracking-[0.08em] text-blue-bright hover:text-bone">
                    {openChunk === ev.chunk_id ? "hide source ▾" : "view in source ▸"}
                  </button>
                ) : null}
              </div>
              {openChunk === ev.chunk_id && ev.chunk_id ? <ChunkContextView chunkId={ev.chunk_id} /> : null}
            </div>
          ))}
        </div>
      ) : null}

      {/* direct open-in-source for messages / authority quotes */}
      {item.chunk_id ? (
        <div className="mt-2">
          <button type="button" onClick={() => setOpenChunk(openChunk === item.chunk_id ? null : (item.chunk_id ?? null))}
            className="font-display text-sm font-light uppercase tracking-[0.08em] text-blue-bright hover:text-bone">
            {openChunk === item.chunk_id ? "hide source ▾" : "view in source ▸"}
          </button>
          {openChunk === item.chunk_id ? <ChunkContextView chunkId={item.chunk_id} /> : null}
        </div>
      ) : null}
    </div>
  );
}

function ChunkContextView({ chunkId }: { chunkId: string }) {
  const { data, isLoading, error } = useChunkContext(chunkId, true);
  if (isLoading) return <p className="mt-2 font-display text-xs uppercase tracking-[0.1em] text-bone-mute">// opening source…</p>;
  if (error) return <p className="mt-2 font-display text-xs text-ember-bright">// {error.message}</p>;
  if (!data) return null;
  return (
    <div className="mt-2 rounded border border-steel-dark/70 bg-charcoal/50 p-3">
      {data.source_label ? (
        <p className="mb-1.5 font-display text-xs font-light uppercase tracking-[0.1em] text-bone-mute">
          {data.source_label}{data.occurred_on ? ` · ${data.occurred_on}` : ""}
        </p>
      ) : null}
      <div className="space-y-1.5">
        {data.chunks.map((ch) => (
          <p
            key={ch.chunk_index}
            className={`whitespace-pre-wrap font-body text-xs leading-snug ${ch.is_center ? "text-bone" : "text-bone-mute"}`}
          >
            {ch.body}
          </p>
        ))}
      </div>
    </div>
  );
}
